import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Download, Images, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { axiosInstance } from "@/config/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const mb = (b) => `${((b || 0) / 1048576).toFixed(1)} MB`;

export const AlbumDownloadCard = () => {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);

  const poll = useCallback(() => {
    axiosInstance.get("/parent/album-export/latest").then((r) => {
      const j = r.data.job;
      setJob(j);
      clearTimeout(timer.current);
      if (j && ["queued", "running"].includes(j.status)) timer.current = setTimeout(poll, 2000);
    }).catch(() => {});
  }, []);
  useEffect(() => { poll(); return () => clearTimeout(timer.current); }, [poll]);

  const start = async () => {
    setStarting(true);
    try {
      const r = await axiosInstance.post("/parent/album-export");
      setJob(r.data);
      timer.current = setTimeout(poll, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ralat menyediakan album");
    } finally {
      setStarting(false);
    }
  };

  const running = job && ["queued", "running"].includes(job.status);
  const pct = job?.total ? Math.round((job.done / job.total) * 100) : 0;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="mb-6 rounded-3xl bg-white border-2 border-purple-200 p-5 shadow-lg" data-testid="album-download-card">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-400 to-pink-400 text-white flex items-center justify-center shrink-0">
          <Images className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <p className="font-black text-lg text-gray-800">Simpan Kenangan Si Kecil</p>
          <p className="text-sm text-gray-600">Muat turun semua foto album dan aktiviti anak anda dalam satu fail ZIP sebelum akses berakhir.</p>
        </div>
        {job?.status === "done" ? (
          <div className="flex gap-2 shrink-0">
            <Button asChild className="rounded-2xl bg-purple-600 hover:bg-purple-700 text-white font-black" data-testid="album-download-btn">
              <a href={`${API}/parent/album-export/${job.id}/file?key=${job.key}`}><Download className="w-4 h-4 mr-1" /> Muat Turun ({mb(job.size)})</a>
            </Button>
            <Button variant="outline" onClick={start} disabled={starting} className="rounded-2xl" data-testid="album-download-redo-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <Button onClick={start} disabled={starting || running} className="shrink-0 rounded-2xl bg-purple-600 hover:bg-purple-700 text-white font-black" data-testid="album-download-start-btn">
            {starting || running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
            {running ? "Sedang disediakan..." : "Muat Turun Semua Foto"}
          </Button>
        )}
      </div>
      {running && (
        <div className="mt-4" data-testid="album-download-progress">
          <Progress value={pct} className="h-2" />
          <p className="text-xs text-gray-500 mt-1">{job.total ? `${job.done} / ${job.total} foto (${pct}%)` : "Mengumpul senarai foto..."}</p>
        </div>
      )}
      {job?.status === "done" && (
        <p className="text-xs text-gray-500 mt-3" data-testid="album-download-summary">
          {job.saved} foto sedia{job.failed ? ` · ${job.failed} gagal dimuat` : ""} · pautan sah 24 jam
        </p>
      )}
      {["failed", "empty"].includes(job?.status) && (
        <p className="text-xs text-rose-500 mt-3" data-testid="album-download-error">
          {job.status === "empty" ? "Tiada foto untuk dimuat turun." : "Gagal menyediakan fail. Sila cuba lagi."}
        </p>
      )}
    </motion.div>
  );
};
